import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, User } from "lucide-react";
import logo from "@/assets/logo.png";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n/LanguageContext";
import { useCustomerAuth } from "@/auth/CustomerAuthContext";
import { trackCtaClick } from "@/lib/analytics";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

/**
 * Solutions group — surfaced via a desktop dropdown and a mobile
 * collapsible. Each item has a short descriptor shown in the dropdown.
 */
const SOLUTIONS_NAV = [
  {
    to: "/fast-access-power-components",
    key: "nav.fastAccess",
    descKey: "nav.fastAccess.desc",
  },
  {
    to: "/pre-configured-power-packages",
    key: "nav.kits",
    descKey: "nav.kits.desc",
  },
  {
    to: "/power-systems",
    key: "nav.engineered",
    descKey: "nav.engineered.desc",
  },
] as const;

// Top-level items (What We Source, How It Works) are rendered inline
// alongside the Solutions dropdown — no shared array needed.

const Header = () => {
  const { pathname } = useLocation();
  const { t, lang, localePath } = useLang();
  const { user } = useCustomerAuth();
  const [hideAtBottom, setHideAtBottom] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const accountHref = localePath(user ? "/account" : "/account/auth");
  const accountLabel = user
    ? { en: "Account", es: "Mi cuenta", pt: "Minha conta" }[lang]
    : { en: "Sign in", es: "Iniciar sesión", pt: "Entrar" }[lang];
  const mobileAccountLabel = user
    ? { en: "My account", es: "Mi cuenta", pt: "Minha conta" }[lang]
    : {
        en: "Sign in / Create account",
        es: "Iniciar sesión / Crear cuenta",
        pt: "Entrar / Criar conta",
      }[lang];

  const handleLogoClick = () => {
    if (pathname === "/" || pathname === "/es" || pathname === "/pt") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.dispatchEvent(new Event("home:reset"));
    }
  };

  useEffect(() => {
    // Desktop only: hide sticky header when footer enters viewport
    const mql = window.matchMedia("(min-width: 768px)");
    let ticking = false;

    const update = () => {
      ticking = false;
      if (!mql.matches) {
        setHideAtBottom(false);
        return;
      }
      const footer = document.querySelector("footer");
      if (!footer) return;
      const rect = footer.getBoundingClientRect();
      setHideAtBottom(rect.top < window.innerHeight);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathname]);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-border bg-background transition-transform duration-300 ${
        hideAtBottom ? "md:-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="container flex h-14 items-center justify-between gap-2 px-4 sm:h-16 md:h-24">
        <Link
          to={localePath("/")}
          onClick={handleLogoClick}
          aria-label="Switzh home"
          className="flex shrink-0 items-center"
        >
          <img
            src={logo}
            alt="Switzh — U.S. Electrical Sourcing. Delivered Across LATAM."
            className="h-10 w-auto sm:h-11 md:h-14"
            width={1397}
            height={318}
          />
        </Link>

        <nav className="flex items-center gap-1.5 sm:gap-2 md:gap-8">
          {/* Desktop nav — What We Source */}
          <Link
            to={localePath("/what-we-source")}
            className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:inline-block"
          >
            {t("nav.whatWeSource")}
          </Link>

          {/* Desktop Solutions dropdown */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto bg-transparent px-0 text-sm font-medium text-muted-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent">
                  {t("nav.solutions")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-1 p-3">
                    {SOLUTIONS_NAV.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={localePath(item.to)}
                          className="block rounded-md p-3 transition-colors hover:bg-secondary focus:bg-secondary focus:outline-none"
                        >
                          <div className="text-sm font-semibold text-foreground">
                            {t(item.key)}
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {t(item.descKey)}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Desktop nav — How It Works */}
          <Link
            to={localePath("/how-it-works")}
            className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:inline-block"
          >
            {t("nav.howItWorks")}
          </Link>

          {/* Language switcher — desktop only; on mobile it lives in the menu */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>

          {/* Account icon — desktop (removed per request) */}

          <Button asChild size="sm" className="h-9 px-3 text-xs font-semibold sm:h-10 sm:px-4 sm:text-sm">
            <Link to={localePath("/request-quote")} onClick={() => trackCtaClick("header", "request_quote")}>
              {t("nav.getQuote")}
            </Link>
          </Button>

          {/* Mobile menu trigger — hidden on md+ */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                className="h-11 w-11 md:hidden [&_svg]:!size-7"
              >
                <Menu className="h-7 w-7" strokeWidth={1.75} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0">
              <SheetHeader className="border-b border-border px-5 py-4 text-left">
                <SheetTitle className="sr-only">{t("nav.solutions")}</SheetTitle>
                <div className="flex items-center justify-between gap-3">
                  <Link
                    to={localePath("/supplier-login")}
                    className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {{ en: "Supplier login", es: "Acceso proveedores", pt: "Acesso fornecedores" }[lang]}
                  </Link>
                  <LanguageSwitcher variant="full" align="end" />
                </div>
              </SheetHeader>
              <nav className="flex flex-col px-2 py-3">
                <Link
                  to={localePath("/what-we-source")}
                  className="rounded-md px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("nav.whatWeSource")}
                </Link>

                {/* Solutions group — mirrors desktop dropdown with subtext */}
                <div className="mt-1 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("nav.solutions")}
                </div>
                <div className="flex flex-col gap-0.5">
                  {SOLUTIONS_NAV.map((item) => (
                    <Link
                      key={item.to}
                      to={localePath(item.to)}
                      className="rounded-md px-3 py-2.5 transition-colors hover:bg-secondary"
                      onClick={() => setMobileOpen(false)}
                    >
                      <div className="text-sm font-semibold text-foreground">
                        {t(item.key)}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {t(item.descKey)}
                      </p>
                    </Link>
                  ))}
                </div>

                <div className="my-2 h-px bg-border" />

                <Link
                  to={localePath("/how-it-works")}
                  className="rounded-md px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("nav.howItWorks")}
                </Link>

                <Link
                  to={accountHref}
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  {mobileAccountLabel}
                </Link>
              </nav>
              <div className="mt-2 border-t border-border px-5 py-4">
                <Button
                  asChild
                  className="w-full"
                  onClick={() => {
                    trackCtaClick("header_mobile", "request_quote");
                    setMobileOpen(false);
                  }}
                >
                  <Link to={localePath("/request-quote")}>{t("nav.getQuote")}</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
